import * as Sentry from '@sentry/react';
import { APP_ENV, isProduction, isStaging } from '@/lib/env';
import { scrubRecord, scrubText, stripUrl } from '@/lib/privacyScrub';

const dsn = import.meta.env.VITE_SENTRY_DSN;

/**
 * Error monitoring + light performance tracing (#478, finding 18.2 — this
 * was tracesSampleRate: 0, effectively error-capture only, with no way to
 * see request/transaction latency in an incident). Sampled low (10%) since
 * this is a low-traffic marketplace app, not a high-volume service where a
 * lower rate would be needed to control cost/ingest volume. Session Replay
 * is deliberately still NOT enabled — that's a distinct, much higher-PII-risk
 * integration (records DOM/user input) and adding it isn't part of this
 * finding; a separate decision if ever wanted.
 */
export function initSentry(): void {
  if (!dsn || !(isProduction || isStaging)) return;

  Sentry.init({
    dsn,
    environment: APP_ENV,
    // Group errors per deploy so a regression is attributable to a release
    // (#478 18.2). Commit SHA injected at build via vite define.
    release: import.meta.env.VITE_BUILD_SHA || undefined,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
    // M11: no IP address, cookies or user details by default, and no query
    // strings or fragments anywhere (auth codes, reset tokens and PostgREST
    // filter values such as ?phone=eq.… travel there).
    sendDefaultPii: false,
    beforeSend: scrubSentryEvent,
    beforeSendTransaction: scrubSentryEvent,
    beforeSendSpan: scrubSentrySpan,
    beforeBreadcrumb: scrubSentryBreadcrumb,
  });
}

type ScrubbableSpan = { description?: string; data?: Record<string, unknown> };

type ScrubbableEvent = {
  request?: { url?: string; query_string?: unknown; cookies?: unknown; headers?: unknown };
  extra?: Record<string, unknown>;
  transaction?: string;
  message?: string;
  exception?: { values?: Array<{ value?: string; stacktrace?: { frames?: Array<{ filename?: string; abs_path?: string }> } }> };
  logentry?: { message?: string; params?: unknown[] };
  spans?: ScrubbableSpan[];
  contexts?: { trace?: ScrubbableSpan };
};

// Span attributes that hold a URL's query or fragment alone.
const SPAN_QUERY_KEYS = ['http.query', 'http.fragment', 'url.query', 'url.fragment'];
const LOOKS_LIKE_URL = /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i;
// An element as Sentry describes it (htmlTreeAsString), in web-vital and
// interaction spans: button[aria-label="Message Jane"], img[alt="…"]. The
// attribute's name stays, its value goes: labels here can name a person.
const DOM_ATTR_VALUE = /\[([\w:-]+)=(?:"[^"]*"|'[^']*'|[^\]]*)\]/g;

function scrubSpanText(value: string): string {
  if (LOOKS_LIKE_URL.test(value)) return stripUrl(value);
  return scrubText(stripUrl(value).replace(DOM_ATTR_VALUE, '[$1]'));
}

/**
 * A span's description ("GET https://…?q=…", an element selector) and every
 * string attribute, not a list of known keys: performance spans keep URLs
 * under lcp.url, code.filepath, resource names and more, each version
 * adding some (M11 / Codex on #1147). URLs lose query and fragment; other
 * text loses element attribute values, emails and numbers.
 */
export function scrubSentrySpan<T>(span: T): T {
  const sp = span as ScrubbableSpan | null;
  if (!sp) return span;
  if (typeof sp.description === 'string') sp.description = scrubSpanText(sp.description);
  const data = sp.data;
  if (data) {
    for (const key of SPAN_QUERY_KEYS) delete data[key];
    for (const [key, value] of Object.entries(data)) {
      if (typeof value === 'string') data[key] = scrubSpanText(value);
      else if (Array.isArray(value)) data[key] = value.map((v) => (typeof v === 'string' ? scrubSpanText(v) : v));
    }
  }
  return span;
}

export function scrubSentryEvent<T>(event: T): T {
  const e = event as ScrubbableEvent;
  if (e.request) {
    if (typeof e.request.url === 'string') e.request.url = stripUrl(e.request.url);
    delete e.request.query_string;
    delete e.request.cookies;
    delete e.request.headers;
  }
  if (e.extra) e.extra = scrubRecord(e.extra);
  if (typeof e.transaction === 'string') e.transaction = stripUrl(e.transaction);
  // The error itself: a server or validation message may echo what was
  // submitted. Free text, so scrubText (emails, numbers, URL queries).
  if (typeof e.message === 'string') e.message = scrubText(e.message);
  e.exception?.values?.forEach((v) => {
    if (typeof v.value === 'string') v.value = scrubText(v.value);
    // Frames name the script they ran in, query included (Maps loads with
    // ?key=…).
    v.stacktrace?.frames?.forEach((f) => {
      if (typeof f.filename === 'string') f.filename = stripUrl(f.filename);
      if (typeof f.abs_path === 'string') f.abs_path = stripUrl(f.abs_path);
    });
  });
  if (e.logentry) {
    if (typeof e.logentry.message === 'string') e.logentry.message = scrubText(e.logentry.message);
    delete e.logentry.params;
  }
  // A transaction carries its child spans, and its own root span as the
  // trace context: both hold request URLs (M11 / Codex on #1147).
  if (Array.isArray(e.spans)) e.spans.forEach(scrubSentrySpan);
  if (e.contexts?.trace) scrubSentrySpan(e.contexts.trace);
  return event;
}

export function scrubSentryBreadcrumb<T>(breadcrumb: T): T | null {
  // Console breadcrumbs carry whatever was logged, arguments included
  // (BookingFlow logs the caught submission error just before reporting it).
  // Release builds drop console calls already; this covers the rest.
  const category = (breadcrumb as { category?: string } | null)?.category ?? '';
  if (category === 'console') return null;
  // ui.click / ui.input describe the element, aria-label and text included,
  // and labels here can name a person ("Message Jane", a counterparty's
  // call button). The page and the error say enough without them.
  if (category.startsWith('ui.')) return null;
  const data = (breadcrumb as { data?: Record<string, unknown> } | null)?.data;
  if (data) {
    for (const key of ['url', 'from', 'to']) {
      if (typeof data[key] === 'string') data[key] = stripUrl(data[key] as string);
    }
  }
  const crumb = breadcrumb as { message?: string } | null;
  if (crumb && typeof crumb.message === 'string') crumb.message = scrubText(crumb.message);
  return breadcrumb;
}

export { Sentry };
